import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import ICar from '../images/ICar.svg';
import IShop from '../images/IShop.svg';
import IMoon from '../images/IMoon.svg';
import ISun from '../images/ISun.svg';
import LMlogo from '../images/LMlogo.png';
import AvatarImage from './AvatarImage';
import '../styles/HeaderFooterSidebar.css';
import backendUrl from '../BackendUrl';
import { UserContext } from '../context/UserContext';

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [vehicleClasses, setVehicleClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState(null);
  const [models, setModels] = useState([]);
  const [isVehiclesDropdownOpen, setIsVehiclesDropdownOpen] = useState(false);
  const [isShopDropdownOpen, setIsShopDropdownOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // Profile logic
  const { user, signIn, signOut, theme, setTheme } = useContext(UserContext);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const profileBtnRef = useRef(null);

  useEffect(() => {
    axios
      .get(`${backendUrl}/api/vehicles/get-class`)
      .then((response) => setVehicleClasses(response.data.filter(item => item.ready)))
      .catch((error) => console.error('Error fetching vehicle classes:', error));
  }, []);

  useEffect(() => {
    if (selectedClassId) {
      axios
        .get(`${backendUrl}/api/vehicles/get-model`, {
          params: { vehicleClassId: selectedClassId },
        })
        .then((response) => setModels(response.data.filter(item => item.ready)))
        .catch((error) => console.error('Error fetching models:', error));
    } else {
      setModels([]);
    }
  }, [selectedClassId]);

  // Collapse sidebar on route change
  useEffect(() => {
    setExpanded(false);
    setIsVehiclesDropdownOpen(false);
    setIsShopDropdownOpen(false);
    setIsProfileDropdownOpen(false);
    setSelectedClassId(null);
  }, [location.pathname]);

  // Profile dropdown outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        profileBtnRef.current &&
        !profileBtnRef.current.contains(event.target)
      ) {
        setIsProfileDropdownOpen(false);
      }
    };
    if (isProfileDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProfileDropdownOpen]);

  // Dropdown toggles
  const toggleVehiclesDropdown = () => {
    setIsVehiclesDropdownOpen((prev) => !prev);
    setIsShopDropdownOpen(false);
    setIsProfileDropdownOpen(false);
    setSelectedClassId(null);
  };

  const toggleShopDropdown = () => {
    setIsShopDropdownOpen((prev) => !prev);
    setIsVehiclesDropdownOpen(false);
    setIsProfileDropdownOpen(false);
    setSelectedClassId(null);
  };

  const toggleProfileDropdown = () => {
    setIsProfileDropdownOpen((prev) => !prev);
    setIsVehiclesDropdownOpen(false);
    setIsShopDropdownOpen(false);
    setSelectedClassId(null);
  };

  // Vehicles sub-dropdown
  const handleClassClick = (vehicleClass) => {
    if (selectedClassId === vehicleClass._id) {
      setSelectedClassId(null);
    } else {
      setSelectedClassId(vehicleClass._id);
    }
  };

  // Profile sign-in
  const handleSignIn = async () => {
    try {
      const result = await signIn(email, password);

      if (result.success === false && result.verified === false) {
        // Redirect to verify email page
        navigate(`/verify-email?email=${encodeURIComponent(result.email)}`);
        return;
      }

      if (result.success) {
        // Successful sign-in
        setEmail('');
        setPassword('');
        setError('');
        setIsProfileDropdownOpen(false);
      }
    } catch (err) {
      console.error("Sign-in handler error:", err.message);
      setError(err.message || 'Failed to sign in. Please try again.');
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleSignIn();
    }
  };

  const toggleTheme = () => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  };

  return (
    <div style={{ position: 'relative', display: 'flex' }}>
      <aside className={`sidebar${expanded ? ' expanded' : ''}`}>
        <div className="sidebar-top">
          <Link to="/" className="icon-btn">
            <img src={LMlogo} alt="Legacy Motors Logo" className="sidebar-icon" />
            <span className={`sidebar-label${expanded ? ' expanded' : ''}`}>Legacy Motors</span>
          </Link>
        </div>
        <nav className="sidebar-nav">
          {/* Vehicles Dropdown */}
          <div className="sidebar-item">
            <button className="icon-btn" onClick={toggleVehiclesDropdown}>
              <img src={ICar} className="sidebar-icon" />
              <span className={`sidebar-label${expanded ? ' expanded' : ''}`}>Vehicles</span>
            </button>
            {isVehiclesDropdownOpen && (
              <div className="sidebar-dropdown">
                {vehicleClasses.map((vehicleClass, idx) => (
                  <div key={vehicleClass._id || idx} className="sidebar-dropdown-item" style={{ position: 'relative' }}>
                    <a
                      href="#"
                      onClick={e => {
                        e.preventDefault();
                        handleClassClick(vehicleClass);
                      }}
                      className={String(selectedClassId) === String(vehicleClass._id) ? 'active' : ''}
                    >
                      {vehicleClass.class}
                    </a>
                    {/* Sub-dropdown for models */}
                    {String(selectedClassId) === String(vehicleClass._id) && models.length > 0 && (
                      <div className="sidebar-dropdown sidebar-dropdown-sub">
                        {models.map((model, mIdx) => (
                          <Link key={model._id || mIdx} to={`/vehicles/${model._id}`}>
                            {model.model}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          {/* Shop Dropdown */}
          <div className="sidebar-item" style={{ position: 'relative' }}>
            <button className="icon-btn" onClick={toggleShopDropdown}>
              <img src={IShop} className="sidebar-icon" />
              <span className={`sidebar-label${expanded ? ' expanded' : ''}`}>Shop</span>
            </button>
            {isShopDropdownOpen && (
              <div className="sidebar-dropdown">
                <Link to="/parts">Parts</Link>
                <Link to="/offers">Active Offers</Link>
                <Link to="/quote">Request a Quote</Link>
                <Link to="/contact-us">Contact Us</Link>
                <Link to="/buildnprice">Build & Price</Link>
              </div>
            )}
          </div>
        </nav>
        {/* Profile Card Inline */}
        <div className="sidebar-bottom">
          <div
            className="sidebar-item profile-sidebar-item"
            ref={profileBtnRef}
          >
            <button className="icon-btn" onClick={toggleProfileDropdown}>
              <div className="sidebar-icon">
                <AvatarImage />
              </div>
              <span className={`sidebar-label${expanded ? ' expanded' : ''}`} style={{ marginLeft: 8 }}>
                {user
                  ? <>
                      <span className={`sidebar-label${expanded ? ' expanded' : ''}`} style={{ marginLeft: 8 }}>{user.firstName} {user.lastName}</span>
                    </>
                  : 'Guest'}
              </span>
            </button>
            {isProfileDropdownOpen && (
              <div>
                {user ? (
                  <div className="sidebar-dropdown">
                    <Link to="/profile">Edit Profile</Link>
                    {user.role === 'admin' && <Link to="/edit-website">Edit Website</Link>}
                    <a
                      href="#logout"
                      onClick={e => {
                        e.preventDefault();
                        signOut();
                      }}
                    >
                      Logout
                    </a>
                  </div>
                ) : (
                  <div className="sidebar-dropdown">
                    <input
                      type="email"
                      placeholder="Email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onKeyDown={handleKeyDown}
                    />
                    <input
                      type="password"
                      placeholder="Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onKeyDown={handleKeyDown}
                    />
                    <a
                      href="#signin"
                      onClick={e => {
                        e.preventDefault();
                        handleSignIn();
                      }}
                    >
                      Sign In
                    </a>
                    {error && <div className="error-message">{error}</div>}
                    <Link to="/signup">Sign Up</Link>
                    <Link to="/reset-password" style={{ display: 'block', marginTop: '4px' }}>
                      Forgot Password?
                    </Link>
                  </div>
                )}
              </div>
            )}
          </div>
          {/* Theme Toggle Button */}
          <div className="theme-toggle-bottom">
            <button
              className="icon-btn"
              onClick={toggleTheme}
              aria-label="Toggle light/dark mode"
            >
              {theme === 'light' ? (
                <>
                  <img src={IMoon} className="sidebar-icon" />
                  <span className={`sidebar-label${expanded ? ' expanded' : ''}`}>Dark Mode</span>
                </>
              ) : (
                <>
                  <img src={ISun} className="sidebar-icon" />
                  <span className={`sidebar-label${expanded ? ' expanded' : ''}`}>Light Mode</span>
                </>
              )}
            </button>
          </div>
        </div>
      </aside>
      {/* Toggle Button */}
      <button
        className={`sidebar-toggle-btn${expanded ? ' expanded' : ''}`}
        onClick={() => setExpanded((prev) => !prev)}
        aria-label={expanded ? 'Collapse sidebar' : 'Expand sidebar'}
      >
        {expanded ? <p>‹</p> : <p>›</p>}
      </button>
    </div>
  );
};

export default Sidebar;